/**
 * passkey-device.ts — per-DEVICE passkey bookkeeping (W2-02, 2026-06-11).
 *
 * WHY this module exists: `signInWithPasskey` only *uses* existing passkeys.
 * On a fresh device with no local credential, leading with "Sign in with
 * Windows Hello" sends the browser straight to the cross-device QR — the
 * W2-02 "surprise QR" bug. Login needs to know, BEFORE rendering the CTA,
 * whether THIS device holds a platform passkey for the entered user. That is
 * client-side-only knowledge (Cognito's ListWebAuthnCredentials requires a
 * signed-in access token, which Login doesn't have yet), so we track it in
 * localStorage at registration time.
 *
 * Design (per docs/audits/PRE-BUILD-AUDIT-2026-06-11-passkey-ux.md §5.1):
 *  - `nexus_platform_passkeys` = JSON map { [lowercased email]: credentialId }.
 *    PER-USER, so user B on user A's shared device gets the honest
 *    password-primary CTA and can still one-tap enroll their own passkey.
 *    Storing the credentialId (not a boolean) also powers the Settings list's
 *    "this device" badge and lets Remove clear the right local entry.
 *  - Legacy `nexus_has_platform_passkey === '1'` (auth.ts pre-W2-02 boolean,
 *    set by every successful registerPasskey since NS14) is read as a
 *    DEVICE-LEVEL fallback: "some passkey exists here, user unknown". This
 *    keeps the operator's already-enrolled PC on the no-QR path. The legacy
 *    key is still written by registerPasskey for back-compat readers.
 *  - `nexus_passkey_snooze` = JSON map { [lowercased email]: epochMs } — the
 *    enroll prompt's per-user "Not now" snooze (30 days).
 *  - `nexus_signin_method` (sessionStorage) = how THIS tab's session began
 *    ('password' | 'passkey'). Written by Login just before the full-page
 *    redirect (sessionStorage survives same-tab window.location.href reloads
 *    and dies with the tab — no stale prompts on later visits). Consumed by
 *    PasskeyEnrollPrompt.
 *
 * Pure module: localStorage/sessionStorage only, no imports from auth.ts /
 * demo.ts (auth.ts imports US — keep this a leaf to avoid cycles). Every
 * storage access is try/catch'd (private browsing, quota).
 */

const MAP_KEY = 'nexus_platform_passkeys';
const LEGACY_FLAG_KEY = 'nexus_has_platform_passkey';
const SNOOZE_KEY = 'nexus_passkey_snooze';
const SIGNIN_METHOD_KEY = 'nexus_signin_method';

/** "Not now" suppresses the enroll prompt for 30 days (audit §5.7). */
export const ENROLL_SNOOZE_MS = 30 * 86_400_000;

export type SigninMethod = 'password' | 'passkey';

/** Lowercase + trim — emails are the user key; Cognito treats them case-insensitively. */
function normalizeUserKey(userKey: string | null | undefined): string {
  return (userKey || '').trim().toLowerCase();
}

/** Read a JSON string-map from storage; corrupt/missing values degrade to {}. */
function readMap(storageKey: string): Record<string, unknown> {
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
  } catch {
    return {};
  }
}

function writeMap(storageKey: string, map: Record<string, unknown>): void {
  try { localStorage.setItem(storageKey, JSON.stringify(map)); } catch { /* private mode — best-effort */ }
}

/** The per-user { email -> credentialId } enrollment map for this device. */
export function getLocalPasskeyMap(): Record<string, string> {
  const map = readMap(MAP_KEY);
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(map)) {
    if (typeof v === 'string' && v) out[k] = v;
  }
  return out;
}

function legacyFlagSet(): boolean {
  try { return localStorage.getItem(LEGACY_FLAG_KEY) === '1'; } catch { return false; }
}

/**
 * Does THIS device hold a platform passkey for `userKey`?
 *
 * - Known user: exact per-user map hit, OR the legacy device-level boolean
 *   (pre-W2-02 enrollments didn't record who enrolled — treat as "a passkey
 *   exists here" rather than punishing the operator's PC with the QR again).
 * - Empty userKey (Login before the email is typed): device-level answer —
 *   any map entry or the legacy flag (audit §5.1 empty-email default).
 */
export function hasLocalPasskey(userKey?: string | null): boolean {
  const key = normalizeUserKey(userKey);
  const map = getLocalPasskeyMap();
  if (!key) return Object.keys(map).length > 0 || legacyFlagSet();
  return !!map[key] || legacyFlagSet();
}

/** Record a successful platform-passkey registration for this user on this device. */
export function recordLocalPasskey(userKey: string, credentialId: string): void {
  const key = normalizeUserKey(userKey);
  if (!key || !credentialId) return;
  const map = getLocalPasskeyMap();
  map[key] = credentialId;
  writeMap(MAP_KEY, map);
}

/** Forget this user's local passkey (e.g. its credential was removed in Settings). */
export function clearLocalPasskey(userKey: string): void {
  const key = normalizeUserKey(userKey);
  if (!key) return;
  const map = getLocalPasskeyMap();
  if (!(key in map)) return;
  delete map[key];
  writeMap(MAP_KEY, map);
}

/**
 * Is `credentialId` the passkey THIS device enrolled for `userKey`?
 * Powers the Settings list's "this device" badge (match the Cognito
 * ListWebAuthnCredentials row against the locally-recorded id) and the
 * decide-whether-to-clear-local-state step after a Remove.
 */
export function isThisDeviceCredential(userKey: string | null | undefined, credentialId: string): boolean {
  const key = normalizeUserKey(userKey);
  if (!key || !credentialId) return false;
  return getLocalPasskeyMap()[key] === credentialId;
}

/** Epoch-ms until which the enroll prompt is snoozed for this user, or null. */
export function getEnrollSnoozeUntil(userKey: string | null | undefined): number | null {
  const key = normalizeUserKey(userKey);
  if (!key) return null;
  const v = readMap(SNOOZE_KEY)[key];
  return typeof v === 'number' && Number.isFinite(v) && v > 0 ? v : null;
}

/** "Not now" — suppress the enroll prompt for this user for 30 days. */
export function snoozeEnrollPrompt(userKey: string, now: number = Date.now()): void {
  const key = normalizeUserKey(userKey);
  if (!key) return;
  const map = readMap(SNOOZE_KEY);
  map[key] = now + ENROLL_SNOOZE_MS;
  writeMap(SNOOZE_KEY, map);
}

export interface EnrollPromptInputs {
  /** How this tab's session began — only 'password' sign-ins get the offer. */
  signinMethod: string | null | undefined;
  /** isPasskeySupported() — no offer on browsers that can't do WebAuthn. */
  supported: boolean;
  /** isDemoSession() — NEVER offer on the shared demo account (a demo
   *  visitor's passkey would become a cross-visitor credential, audit §5.3). */
  isDemo: boolean;
  /** hasLocalPasskey(user.email) — already enrolled here, nothing to offer. */
  hasLocal: boolean;
  /** getEnrollSnoozeUntil(user.email) — per-user "Not now" suppression. */
  snoozedUntil: number | null | undefined;
  now: number;
}

/**
 * The W2-02 Step-2 decision: offer one-tap passkey enrollment right after a
 * sign-in IFF every gate passes. Pure so the full matrix is unit-testable.
 * Snooze boundary: suppressed strictly WHILE now < snoozedUntil — at exactly
 * the 30-day mark the offer returns.
 */
export function shouldOfferEnrollPrompt(inputs: EnrollPromptInputs): boolean {
  const { signinMethod, supported, isDemo, hasLocal, snoozedUntil, now } = inputs;
  if (signinMethod !== 'password') return false;
  if (!supported) return false;
  if (isDemo) return false;
  if (hasLocal) return false;
  if (typeof snoozedUntil === 'number' && now < snoozedUntil) return false;
  return true;
}

// ============================================================
// Sign-in-method marker (sessionStorage) — written by Login pre-redirect,
// consumed by PasskeyEnrollPrompt post-redirect.
// ============================================================

export function recordSigninMethod(method: SigninMethod): void {
  try { sessionStorage.setItem(SIGNIN_METHOD_KEY, method); } catch { /* best-effort */ }
}

/** Read the marker WITHOUT consuming it (the prompt clears it on action). */
export function peekSigninMethod(): string | null {
  try { return sessionStorage.getItem(SIGNIN_METHOD_KEY); } catch { return null; }
}

export function clearSigninMethod(): void {
  try { sessionStorage.removeItem(SIGNIN_METHOD_KEY); } catch { /* best-effort */ }
}
